import {
	SettlementCurrency,
	SettlementRowStatus,
	SettlementRowType,
	SettlementMonthSummaryType,
} from '../types/main/SettlementType'

const num = (v: unknown): number =>
	v === null || v === undefined || v === '' ? 0 : Number(v)
const nullableNum = (v: unknown): number | null =>
	v === null || v === undefined || v === '' ? null : Number(v)
const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v))

/** 정산 행 — API 응답(카멜) 그대로. 그리드 행 id = 주문번호 */
class SettlementRow implements SettlementRowType {
	constructor(a: Partial<SettlementRowType> = {}) {
		this.orderId = str(a.orderId)
		this.id = this.orderId
		this.orderedAt = str(a.orderedAt)
		this.paidAt = str(a.paidAt)
		this.paidYn = a.paidYn === undefined ? 1 : num(a.paidYn)
		this.preparedAt = str(a.preparedAt)
		this.channel = a.channel === 'wholesale' ? 'wholesale' : 'retail'
		this.currency = (a.currency === 'KRW' ? 'KRW' : 'USD') as SettlementCurrency
		this.usdKrwRate = num(a.usdKrwRate)
		this.customerId = str(a.customerId)
		this.customerEmail = str(a.customerEmail)
		this.partyName = str(a.partyName)
		this.itemSummary = str(a.itemSummary)
		this.status = a.status ?? 'sold'
		this.shippingCost = nullableNum(a.shippingCost)
		this.dutyCost = nullableNum(a.dutyCost)
		this.vatAmount = num(a.vatAmount)
		this.shippingAmount = num(a.shippingAmount)
		this.dutyAmount = num(a.dutyAmount)
		this.paidAmount = num(a.paidAmount)
		this.hasSettlement = a.hasSettlement === true
		this.saleAmount = num(a.saleAmount)
		this.materialCost = num(a.materialCost)
		this.costMissingQty = num(a.costMissingQty)
		this.extraCost = num(a.extraCost)
		this.marginAmount = num(a.marginAmount)
		this.eligibleYn = a.eligibleYn === undefined ? 1 : num(a.eligibleYn)
		this.vatReserve = num(a.vatReserve)
		this.donationReserve = num(a.donationReserve)
		this.ruleVersion = str(a.ruleVersion)
		this.computedAt = str(a.computedAt)
		this.vatPaidYn = num(a.vatPaidYn)
		this.vatPaidAt = str(a.vatPaidAt)
		this.vatPaidBy = str(a.vatPaidBy)
		this.donationPaidYn = num(a.donationPaidYn)
		this.donationPaidAt = str(a.donationPaidAt)
		this.donationPaidBy = str(a.donationPaidBy)
		this.taxInvoiceYn = num(a.taxInvoiceYn)
		this.taxInvoiceAt = str(a.taxInvoiceAt)
		this.taxInvoiceNo = str(a.taxInvoiceNo)
		this.memo = str(a.memo)
		this.updatedAt = str(a.updatedAt)
		this.updatedBy = str(a.updatedBy)
		this.locked = this.vatPaidYn === 1 || this.donationPaidYn === 1
	}
	/** 그리드 행 식별자 = 주문번호 */
	id: string
	orderId: string
	orderedAt: string
	paidAt: string
	paidYn: number
	preparedAt: string
	channel: 'retail' | 'wholesale'
	currency: SettlementCurrency
	usdKrwRate: number
	customerId: string
	customerEmail: string
	partyName: string
	itemSummary: string
	status: SettlementRowStatus
	shippingCost: number | null
	dutyCost: number | null
	vatAmount: number
	shippingAmount: number
	dutyAmount: number
	paidAmount: number
	hasSettlement: boolean
	saleAmount: number
	materialCost: number
	costMissingQty: number
	extraCost: number
	marginAmount: number
	eligibleYn: number
	vatReserve: number
	donationReserve: number
	ruleVersion: string
	computedAt: string
	vatPaidYn: number
	vatPaidAt: string
	vatPaidBy: string
	donationPaidYn: number
	donationPaidAt: string
	donationPaidBy: string
	taxInvoiceYn: number
	taxInvoiceAt: string
	taxInvoiceNo: string
	memo: string
	updatedAt: string
	updatedBy: string
	locked: boolean
}

class SettlementMonthSummary implements SettlementMonthSummaryType {
	constructor(a: Partial<SettlementMonthSummaryType> = {}) {
		this.month = str(a.month)
		this.currency = (a.currency === 'KRW' ? 'KRW' : 'USD') as SettlementCurrency
		this.orderCount = num(a.orderCount)
		this.eligibleCount = num(a.eligibleCount)
		this.awaitingCount = num(a.awaitingCount)
		this.awaitingSale = num(a.awaitingSale)
		this.saleTotal = num(a.saleTotal)
		this.vatTotal = num(a.vatTotal)
		this.vatPaid = num(a.vatPaid)
		this.vatUnpaid = num(a.vatUnpaid)
		this.donationTotal = num(a.donationTotal)
		this.donationPaid = num(a.donationPaid)
		this.donationUnpaid = num(a.donationUnpaid)
		this.taxInvoiceCount = num(a.taxInvoiceCount)
		this.costMissingCount = num(a.costMissingCount)
		this.awaitingSaleKrw = num(a.awaitingSaleKrw)
		this.saleTotalKrw = num(a.saleTotalKrw)
		this.vatTotalKrw = num(a.vatTotalKrw)
		this.vatPaidKrw = num(a.vatPaidKrw)
		this.vatUnpaidKrw = num(a.vatUnpaidKrw)
		this.donationTotalKrw = num(a.donationTotalKrw)
		this.donationPaidKrw = num(a.donationPaidKrw)
		this.donationUnpaidKrw = num(a.donationUnpaidKrw)
	}
	month: string
	currency: SettlementCurrency
	orderCount: number
	eligibleCount: number
	awaitingCount: number
	awaitingSale: number
	saleTotal: number
	vatTotal: number
	vatPaid: number
	vatUnpaid: number
	donationTotal: number
	donationPaid: number
	donationUnpaid: number
	taxInvoiceCount: number
	costMissingCount: number
	awaitingSaleKrw: number
	saleTotalKrw: number
	vatTotalKrw: number
	vatPaidKrw: number
	vatUnpaidKrw: number
	donationTotalKrw: number
	donationPaidKrw: number
	donationUnpaidKrw: number
}

export { SettlementRow, SettlementMonthSummary }
